import React from 'react';
import { LanguageContext } from '../i18n/LanguageContext';

/**
 * THE LAST THING BETWEEN A RENDER ERROR AND A WHITE PAGE.
 *
 * React unmounts the whole tree when a component throws during render and nothing catches it -
 * which, on a laptop in front of a customer, is a blank white window with no way forward but the
 * address bar. This catches it and offers the one action that reliably helps: reload.
 *
 * Class component because React only exposes getDerivedStateFromError / componentDidCatch on
 * classes. Nothing else in the app needs to be a class.
 */
export const ERROR_BOUNDARY_TEXT = 'Something went wrong. Reload the page.';

export default class ErrorBoundary extends React.Component {
  // A class cannot call useT(); contextType hands it the same { t } the hooks read.
  static contextType = LanguageContext;

  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch() {
    // Keep the app-owned diagnostic fixed. Exceptions and component stacks may contain PHI.
    // eslint-disable-next-line no-console
    console.error('A screen could not be displayed.');
  }

  handleReload() {
    window.location.reload();
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    const t = this.context?.t || ((key) => key);

    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center px-4">
        <div
          role="alert"
          className="w-full max-w-sm bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm p-6 text-center"
        >
          <span className="material-symbols-outlined text-[36px] text-[#de524c]">error</span>
          <p className="mt-3 text-[15px] font-semibold text-[#0f172a]">{t(ERROR_BOUNDARY_TEXT)}</p>
          <p className="mt-1 text-sm text-[#64748b]">
            {t('If it keeps happening, sign out and sign back in.')}
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="mt-5 h-[42px] px-6 bg-[#0089ff] text-white text-sm font-semibold rounded-[10px] hover:bg-[#0077e6] transition-colors"
          >
            {t('Reload')}
          </button>
        </div>
      </div>
    );
  }
}
